import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ConfirmImportCsv } from '@components/forms/import-csv/confirm-import';
import ReviewImport from '@components/forms/import-csv/review-import';
import { SelectType } from '@components/forms/import-csv/select-type';
import { PROCESS_IMPORT_ROUTE } from '@constants/routes';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { EventContext } from '@contexts/event/event-context';
import { ModalContext } from '@contexts/modal/modal-context';
import { useGetAdmission } from '@hooks/admissions/use-get-admission';
import { ImportFormData } from '@hooks/import/type';
import { useImportCsv } from '@hooks/import/use-import-csv';
import { IMPORT_TYPE_CSV } from '@pages/events/utils/import-process-utils';

import {
  ImportCSVForm,
  ProcessImportCSVStep,
  SPLITTER_COMMA,
  STEP_CONFIRM,
  STEP_INFO,
  STEP_SELECT_TYPE,
} from './process-import-csv.type';

export const ProcessImportCsv = () => {
  const navigate = useNavigate();

  const { toggleModal } = useContext(ModalContext);
  const { setPreventExit } = useContext(ModalContext);
  const {
    clubState: { slug: clubSlug },
    companyState: {
      legacyId: companyLegacyId,
      defaultChannel: { id: defaultChannelId },
    },
  } = useContext(CompanyClubContext);
  const {
    eventState: { id: eventId },
  } = useContext(EventContext);

  const [step, setStep] = useState<ProcessImportCSVStep>(STEP_SELECT_TYPE);
  const [fileName, setFileName] = useState<string>('');
  const [rows, setRows] = useState<Array<string[]>>([]);
  const [localQrCodeIssues, setLocalQrCodeIssues] = useState<{ duplicateRows: number[]; emptyRows: number[] }>({
    duplicateRows: [],
    emptyRows: [],
  });
  const [localFormData, setLocalFormData] = useState<ImportFormData>();
  const formRef = useRef<HTMLFormElement>(null);

  const methods = useForm<ImportCSVForm>({
    defaultValues: {
      eventId,
      offerId: '',
      channelId: defaultChannelId,
      splitter: SPLITTER_COMMA,
    },
    mode: 'onChange',
  });
  const {
    formState: { isDirty },
    watch,
  } = methods;
  const offerId = watch('offerId');
  const channelId = watch('channelId');

  const { data: offer } = useGetAdmission({
    offerId,
    channelId,
    options: {
      enabled: !!offerId && !!eventId,
    },
  });

  const { data: importCsvData, mutate: importCsv } = useImportCsv();

  const handleContinueReviewImport = (
    qrCodeIssues: {
      duplicateRows: number[];
      emptyRows: number[];
    },
    formData: ImportFormData
  ) => {
    setLocalQrCodeIssues(qrCodeIssues);
    setLocalFormData(formData);
    if (qrCodeIssues.duplicateRows.length || qrCodeIssues.emptyRows.length) {
      setStep(STEP_CONFIRM);
    } else if (localFormData) importCsv({ importData: localFormData });
  };

  const handleContinueConfirmImportCsv = () => {
    if (localFormData) importCsv({ importData: localFormData });
  };

  const closeModal = useCallback(() => toggleModal({ isOpen: false, type: '' }), [toggleModal]);

  const createProcessPath = useCallback(
    (importId: string) => {
      if (!importId || !eventId) return;
      return PROCESS_IMPORT_ROUTE.replace(':companyId', companyLegacyId)
        .replace(':clubSlug', clubSlug)
        .replace(':eventId', eventId)
        .replace(':importId', importId)
        .replace(':importType', IMPORT_TYPE_CSV);
    },
    [companyLegacyId, clubSlug, eventId]
  );

  useEffect(() => {
    setPreventExit(isDirty);
  }, [isDirty, setPreventExit]);

  useEffect(() => {
    if (importCsvData) {
      const path = createProcessPath(importCsvData.importId);
      if (path) navigate(path);
      closeModal();
    }
  }, [importCsvData, navigate, eventId, createProcessPath, closeModal]);

  return (
    <form className='flex justify-end flex-col relative' ref={formRef}>
      <FormProvider {...methods}>
        {step === STEP_SELECT_TYPE && (
          <SelectType
            offer={offer}
            onContinue={() => setStep(STEP_INFO)}
            fileName={fileName}
            rows={rows}
            setRows={setRows}
            setFileName={setFileName}
          />
        )}
        {step === STEP_INFO && (
          <ReviewImport columns={rows} onDiscard={closeModal} onContinue={handleContinueReviewImport} />
        )}
        {step === STEP_CONFIRM && (
          <ConfirmImportCsv
            qrWarnings={localQrCodeIssues}
            onContinue={handleContinueConfirmImportCsv}
            onBack={() => setStep(STEP_INFO)}
          />
        )}
      </FormProvider>
    </form>
  );
};
